import React, { useState, useEffect, useRef } from 'react';
import { FaShieldAlt, FaRedo } from 'react-icons/fa';
import { getTranslation } from '../../utils/translations';
import './OtpInput.css';

const OtpInput = ({ 
  length = 6, 
  onComplete, 
  onResend, 
  language, 
  error, 
  disabled = false,
  phoneNumber,
  countryCode,
  resendCooldown = 60 // seconds
}) => {
  const [otp, setOtp] = useState(new Array(length).fill(''));
  const [activeIndex, setActiveIndex] = useState(0);
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);

  // Initialize timer on mount
  useEffect(() => {
    setResendTimer(resendCooldown);
    setCanResend(false);
  }, [resendCooldown]);

  // Countdown timer
  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(timer => {
          if (timer <= 1) {
            setCanResend(true);
            return 0;
          }
          return timer - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Focus first input on mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleChange = (index, value) => {
    if (disabled) return;

    // Only allow digits
    const sanitizedValue = value.replace(/\D/g, '');
    
    if (sanitizedValue.length > 1) {
      // Handle paste
      const pastedCode = sanitizedValue.slice(0, length);
      const newOtp = [...otp];
      
      for (let i = 0; i < pastedCode.length; i++) {
        if (index + i < length) {
          newOtp[index + i] = pastedCode[i];
        }
      }
      
      setOtp(newOtp);
      
      // Focus next empty input or last input
      const nextIndex = Math.min(index + pastedCode.length, length - 1);
      setActiveIndex(nextIndex);
      if (inputRefs.current[nextIndex]) {
        inputRefs.current[nextIndex].focus();
      }
      
      // Check if complete
      if (newOtp.every(digit => digit !== '')) {
        onComplete && onComplete(newOtp.join(''));
      }
    } else {
      // Single character input
      const newOtp = [...otp];
      newOtp[index] = sanitizedValue;
      setOtp(newOtp);
      
      // Move to next input if value entered
      if (sanitizedValue && index < length - 1) {
        setActiveIndex(index + 1);
        inputRefs.current[index + 1]?.focus();
      }
      
      // Check if complete
      if (newOtp.every(digit => digit !== '')) {
        onComplete && onComplete(newOtp.join(''));
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (disabled) return;

    if (e.key === 'Backspace') {
      e.preventDefault();
      const newOtp = [...otp];
      
      if (otp[index]) {
        // Clear current input
        newOtp[index] = '';
        setOtp(newOtp);
      } else if (index > 0) {
        // Move to previous input and clear it
        newOtp[index - 1] = '';
        setOtp(newOtp);
        setActiveIndex(index - 1);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      setActiveIndex(index - 1);
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      setActiveIndex(index + 1);
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleFocus = (index) => {
    setActiveIndex(index);
  };

  const handleResend = () => {
    if (canResend && onResend && !disabled) {
      onResend();
      setResendTimer(resendCooldown);
      setCanResend(false);
      // Clear current OTP
      setOtp(new Array(length).fill(''));
      setActiveIndex(0);
      inputRefs.current[0]?.focus();
    }
  };

  const formatPhoneNumber = (phone, countryCode) => {
    if (!phone) return '';
    
    if (countryCode === '+966') {
      // Format Saudi number: +966 5X XXX XXXX
      const cleaned = phone.replace(/\D/g, '');
      if (cleaned.length === 9) {
        return `${countryCode} ${cleaned.slice(0, 2)} ${cleaned.slice(2, 5)} ${cleaned.slice(5)}`;
      }
    }
    
    return `${countryCode} ${phone}`;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`otp-input-container ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      {/* Header */}
      <div className="otp-header">
        {/* <div className="otp-icon">
          <FaShieldAlt />
        </div> */}
        <h3 className="otp-title">
          {getTranslation('verifyPhone', language)}
        </h3>
        <p className="otp-subtitle">
          {language === 'ar' 
            ? `تم إرسال رمز التحقق إلى ${formatPhoneNumber(phoneNumber, countryCode)}`
            : `Verification code sent to ${formatPhoneNumber(phoneNumber, countryCode)}`
          }
        </p>
      </div>

      {/* OTP Input Fields */}
      <div className="otp-inputs">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={el => inputRefs.current[index] = el}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={e => handleChange(index, e.target.value)}
            onKeyDown={e => handleKeyDown(index, e)}
            onFocus={() => handleFocus(index)}
            className={`otp-digit ${activeIndex === index ? 'active' : ''} ${error ? 'error' : ''}`}
            disabled={disabled}
            autoComplete="one-time-code"
          />
        ))}
      </div>

      {/* Error Message */}
      {error && (
        <div className="otp-error">
          {error}
        </div>
      )}

      {/* Resend Section */}
      <div className="otp-resend">
        {!canResend ? (
          <p className="resend-timer">
            {language === 'ar' 
              ? `يمكنك إعادة الإرسال خلال ${formatTime(resendTimer)}`
              : `Resend code in ${formatTime(resendTimer)}`
            }
          </p>
        ) : (
          <button
            type="button"
            onClick={handleResend}
            className="resend-button"
            disabled={disabled}
          >
            <FaRedo className="resend-icon" />
            {getTranslation('resendCode', language)}
          </button>
        )}
      </div>

      {/* Instructions */}
      <div className="otp-instructions">
        <p>
          {language === 'ar' 
            ? 'لم تستلم الرسالة؟ تحقق من مجلد الرسائل المحذوفة أو انتظر قليلاً'
            : "Didn't receive the message? Check your spam folder or wait a moment"
          }
        </p>
      </div>
    </div>
  );
};

export default OtpInput;
